
import { useState } from 'react';
import { useImageGeneration } from '@/hooks/runware';
import { AvatarStyleType } from './types';
import { toast } from '@/hooks/errors/use-toast';

const AVATAR_PROMPT_ID = '29c298f1-1da1-4e49-967b-c17ba4a82d01';

export const useAvatarGeneration = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { generateImage } = useImageGeneration();

  const getImageTypeId = (style: AvatarStyleType): string => {
    switch (style) {
      case 'photo':
        return 'type-photo';
      case 'art':
        return 'type-art';
      case 'anime':
        return 'type-anime';
      case '3d-render':
        return 'type-3d';
      default:
        return 'type-photo';
    }
  };

  const generateAvatar = async (description: string, style: AvatarStyleType): Promise<string | null> => {
    if (!description.trim()) {
      setError('Please provide a description for your avatar');
      toast({
        title: "Description required",
        description: "Please describe how you want your avatar to look",
        variant: "warning",
      });
      return null;
    }
    
    setIsGenerating(true);
    setError(null);
    
    try {
      const imageType = getImageTypeId(style);
      
      console.log('Generating avatar with params:', {
        basePromptId: AVATAR_PROMPT_ID,
        additionalText: `Create an avatar of ${description}`,
        imageType: imageType
      });
      
      // Generate the image using the consolidated hook with backend prompt building
      const generatedImages = await generateImage({
        basePromptId: AVATAR_PROMPT_ID,
        additionalText: `Create an avatar of ${description}`,
        imageType: imageType,
        width: 256,
        height: 256,
        numberResults: 1,
        format: 'webp',
        quality: 90
      });
      
      if (!generatedImages || generatedImages.length === 0) {
        throw new Error('No images were generated');
      }
      
      const imageUrl = generatedImages[0].imageURL;
      setGeneratedImageUrl(imageUrl);
      
      toast({
        title: "Avatar generated successfully",
        variant: "success",
      });
      
      return imageUrl;
    } catch (err: any) {
      console.error('[useAvatarGeneration] Error generating avatar:', err);
      const errorMessage = err.message || 'An unexpected error occurred';
      
      setError(errorMessage);
      
      toast({
        title: "Failed to generate avatar",
        description: errorMessage,
        variant: "error",
      });
      
      return null;
    } finally {
      setIsGenerating(false);
    }
  };
  
  return {
    generateAvatar,
    isGenerating,
    generatedImageUrl,
    error,
  };
};
